/**
 * Checklist Template Service
 *
 * Phase 6 - Office-configurable checklist templates that auto-attach
 * to jobs based on dealCategory + jobType matching.
 *
 * Collection: settings/checklists/{templateId}
 *
 * Templates define checkpoint lists that are automatically attached to new jobs
 * when the template's dealCategory + jobType match and isDefault is true.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { EnhancedChecklistTemplate, CheckpointTemplate, Checkpoint } from '@/types/schema';

const COLLECTION = 'checklist_templates';

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Create a new checklist template
 */
export async function createChecklistTemplate(
  template: Omit<EnhancedChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const now = new Date().toISOString();
  const docRef = await addDoc(collection(db, COLLECTION), {
    ...template,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

/**
 * Update an existing checklist template
 */
export async function updateChecklistTemplate(
  id: string,
  updates: Partial<Omit<EnhancedChecklistTemplate, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Delete a checklist template
 */
export async function deleteChecklistTemplate(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

/**
 * Duplicate a template with a new name
 */
export async function duplicateChecklistTemplate(
  original: EnhancedChecklistTemplate,
  newName: string,
): Promise<string> {
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...rest } = original;
  return createChecklistTemplate({
    ...rest,
    name: newName,
    isDefault: false, // Duplicated templates are not default by default
  });
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch all templates for a tenant
 */
export async function getTemplatesForTenant(
  tenantId: string,
): Promise<EnhancedChecklistTemplate[]> {
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as EnhancedChecklistTemplate[];
}

/**
 * Find the default template for a given dealCategory + jobType
 */
export async function getDefaultTemplate(
  tenantId: string,
  dealCategory: string,
  jobType: string,
): Promise<EnhancedChecklistTemplate | null> {
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
    where('dealCategory', '==', dealCategory),
    where('jobType', '==', jobType),
    where('isDefault', '==', true),
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as EnhancedChecklistTemplate;
}

/**
 * Subscribe to all templates for a tenant (real-time)
 */
export function subscribeToTemplates(
  tenantId: string,
  callback: (templates: EnhancedChecklistTemplate[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', '==', tenantId),
  );
  return onSnapshot(q, (snapshot) => {
    const templates = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as EnhancedChecklistTemplate[];
    callback(templates);
  });
}

// ============================================================================
// TEMPLATE → CHECKPOINT CONVERSION
// ============================================================================

/**
 * Convert a template's CheckpointTemplate[] into Checkpoint[] for a job.
 * Each CheckpointTemplate becomes a fresh Checkpoint with completed=false.
 */
export function generateCheckpointsFromTemplate(
  template: EnhancedChecklistTemplate,
): Checkpoint[] {
  return template.checkpoints
    .sort((a, b) => a.order - b.order)
    .map((cp: CheckpointTemplate) => ({
      id: cp.id,
      label: cp.label,
      type: cp.type,
      required: cp.required,
      completed: false,
      value: undefined,
      timestamp: undefined,
      completedBy: undefined,
    }));
}

/**
 * Auto-attach the default template's checkpoints to a job.
 * Called during job creation when dealCategory + jobType are known.
 *
 * Returns the checkpoints array to set on the job, or null if no matching template.
 */
export async function getCheckpointsForNewJob(
  tenantId: string,
  dealCategory: string,
  jobType: string,
): Promise<Checkpoint[] | null> {
  const template = await getDefaultTemplate(tenantId, dealCategory, jobType);
  if (!template) return null;
  return generateCheckpointsFromTemplate(template);
}

// ============================================================================
// DEFAULT TEMPLATES (SEED DATA)
// ============================================================================

/**
 * Seed default templates for all 7 deal categories.
 * Call once during initial setup or from admin panel.
 */
export async function seedDefaultTemplates(
  tenantId: string,
  createdBy: string,
): Promise<number> {
  const existing = await getTemplatesForTenant(tenantId);
  if (existing.length > 0) return 0; // Don't seed if templates already exist

  let count = 0;
  for (const template of DEFAULT_TEMPLATES) {
    await createChecklistTemplate({
      ...template,
      tenantId,
      createdBy,
    });
    count++;
  }
  return count;
}

const DEFAULT_TEMPLATES: Omit<EnhancedChecklistTemplate, 'id' | 'createdAt' | 'updatedAt' | 'tenantId' | 'createdBy'>[] = [
  {
    name: 'NBN Install',
    dealCategory: 'NBN',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'nbn_locate_nbn_box', label: 'Locate NTD/NCD box', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'nbn_run_cable', label: 'Run cable to router location', type: 'checkbox', required: true, order: 2, category: 'Setup' },
      { id: 'nbn_connect_modem', label: 'Connect and power on modem', type: 'checkbox', required: true, order: 3, category: 'Setup' },
      { id: 'nbn_configure_wifi', label: 'Configure WiFi SSID and password', type: 'checkbox', required: true, order: 4, category: 'Setup' },
      { id: 'nbn_router_location', label: 'Router location', type: 'text', required: false, order: 5, category: 'Setup', placeholder: 'e.g. Study, wall-mounted' },
      { id: 'nbn_speed_test', label: 'Speed test results', type: 'text', required: true, order: 6, category: 'Testing', placeholder: 'e.g. 100/40 Mbps', suffix: 'Mbps' },
      { id: 'nbn_customer_devices', label: 'Connect customer devices', type: 'checkbox', required: false, order: 7, category: 'Testing' },
      { id: 'nbn_customer_briefed', label: 'Customer shown basics', type: 'checkbox', required: true, order: 8, category: 'Cleanup' },
      { id: 'nbn_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 9, category: 'Cleanup', suffix: '/10' },
      { id: 'nbn_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 10, category: 'Cleanup' },
    ],
  },
  {
    name: 'Starlink Install',
    dealCategory: 'STARLINK',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'sl_mount_dish', label: 'Dish mounted and aligned', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'sl_dish_location', label: 'Dish location', type: 'text', required: false, order: 2, category: 'Setup', placeholder: 'e.g. North-facing roof, above garage' },
      { id: 'sl_dish_confirmed', label: 'Dish position confirmed with customer?', type: 'select', required: false, order: 3, category: 'Setup', options: ['Yes', 'Customer not home', 'No — tech decision'] },
      { id: 'sl_run_cable', label: 'Cable routed and secured', type: 'checkbox', required: true, order: 4, category: 'Setup' },
      { id: 'sl_router_location', label: 'Router location', type: 'text', required: false, order: 5, category: 'Setup', placeholder: 'e.g. Living room, near TV' },
      { id: 'sl_power_on', label: 'Router powered and configured', type: 'checkbox', required: true, order: 6, category: 'Setup' },
      { id: 'sl_wifi_tested', label: 'WiFi network tested on device', type: 'checkbox', required: true, order: 7, category: 'Testing' },
      { id: 'sl_speed_test', label: 'Speed test results', type: 'text', required: true, order: 8, category: 'Testing', placeholder: 'e.g. 250/25 Mbps', suffix: 'Mbps' },
      { id: 'sl_obstruction', label: 'Obstruction check passed', type: 'checkbox', required: true, order: 9, category: 'Testing' },
      { id: 'sl_app_setup', label: 'Customer shown Starlink app', type: 'checkbox', required: false, order: 10, category: 'Cleanup' },
      { id: 'sl_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 11, category: 'Cleanup', suffix: '/10' },
      { id: 'sl_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 12, category: 'Cleanup' },
    ],
  },
  {
    name: 'WiFi Extension Install',
    dealCategory: 'WIFI_EXTENSION',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'wifi_mount_ap', label: 'Access points mounted in planned locations', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'wifi_ap_count', label: 'Number of APs installed', type: 'text', required: false, order: 2, category: 'Setup', placeholder: 'e.g. 3' },
      { id: 'wifi_run_cable', label: 'Cables routed, terminated, and tested', type: 'checkbox', required: true, order: 3, category: 'Setup' },
      { id: 'wifi_configure', label: 'Network configured (SSIDs, VLANs)', type: 'checkbox', required: true, order: 4, category: 'Setup' },
      { id: 'wifi_test_coverage', label: 'Coverage verified in all target areas', type: 'checkbox', required: true, order: 5, category: 'Testing' },
      { id: 'wifi_speed_test', label: 'Speed test results', type: 'text', required: false, order: 6, category: 'Testing', placeholder: 'e.g. 450/45 Mbps at furthest AP', suffix: 'Mbps' },
      { id: 'wifi_management_shown', label: 'Customer shown management interface', type: 'checkbox', required: false, order: 7, category: 'Cleanup' },
      { id: 'wifi_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 8, category: 'Cleanup', suffix: '/10' },
      { id: 'wifi_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 9, category: 'Cleanup' },
    ],
  },
  {
    name: 'Smart Home Install',
    dealCategory: 'SMART_HOME',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'sh_install_devices', label: 'All devices physically installed', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'sh_device_count', label: 'Number of devices installed', type: 'text', required: false, order: 2, category: 'Setup', placeholder: 'e.g. 4' },
      { id: 'sh_network_check', label: 'Network configured', type: 'checkbox', required: true, order: 3, category: 'Setup' },
      { id: 'sh_pair_devices', label: 'All devices paired and online', type: 'checkbox', required: true, order: 4, category: 'Setup' },
      { id: 'sh_test_automations', label: 'Automation scenes configured', type: 'checkbox', required: true, order: 5, category: 'Testing' },
      { id: 'sh_app_access', label: 'Customer has app access and can control devices', type: 'checkbox', required: true, order: 6, category: 'Testing' },
      { id: 'sh_customer_walkthrough', label: 'Customer walkthrough completed', type: 'checkbox', required: true, order: 7, category: 'Cleanup' },
      { id: 'sh_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 8, category: 'Cleanup', suffix: '/10' },
      { id: 'sh_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 9, category: 'Cleanup' },
    ],
  },
  {
    name: 'Security Install',
    dealCategory: 'SECURITY',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'sec_install_sensors', label: 'All sensors/cameras installed', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'sec_camera_count', label: 'Number of cameras installed', type: 'text', required: false, order: 2, category: 'Setup', placeholder: 'e.g. 6' },
      { id: 'sec_hub_configured', label: 'Hub/panel configured and online', type: 'checkbox', required: true, order: 3, category: 'Setup' },
      { id: 'sec_app_setup', label: 'Customer app access set up', type: 'checkbox', required: true, order: 4, category: 'Setup' },
      { id: 'sec_test_zones', label: 'All zones/sensors tested individually', type: 'checkbox', required: true, order: 5, category: 'Testing' },
      { id: 'sec_alarm_tested', label: 'Full alarm test completed', type: 'checkbox', required: true, order: 6, category: 'Testing' },
      { id: 'sec_monitoring', label: 'Monitoring service active (if applicable)', type: 'checkbox', required: false, order: 7, category: 'Testing' },
      { id: 'sec_customer_demo', label: 'Customer walkthrough — arm/disarm/panic', type: 'checkbox', required: true, order: 8, category: 'Cleanup' },
      { id: 'sec_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 9, category: 'Cleanup', suffix: '/10' },
      { id: 'sec_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 10, category: 'Cleanup' },
    ],
  },
  {
    name: 'IT Install',
    dealCategory: 'IT',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'it_install_hardware', label: 'Hardware installed and powered', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'it_software_configured', label: 'Software/accounts configured', type: 'checkbox', required: true, order: 2, category: 'Setup' },
      { id: 'it_cable_management', label: 'Cable management and labelling', type: 'checkbox', required: true, order: 3, category: 'Setup' },
      { id: 'it_network_connected', label: 'Connected to network and tested', type: 'checkbox', required: true, order: 4, category: 'Setup' },
      { id: 'it_speed_test', label: 'Network speed test', type: 'text', required: false, order: 5, category: 'Testing', placeholder: 'e.g. 1Gbps LAN, 200/20 WAN' },
      { id: 'it_data_migrated', label: 'Data migration complete (if applicable)', type: 'checkbox', required: false, order: 6, category: 'Testing' },
      { id: 'it_backup_config', label: 'Backup solution verified', type: 'checkbox', required: false, order: 7, category: 'Testing' },
      { id: 'it_customer_briefed', label: 'Customer briefed on usage', type: 'checkbox', required: true, order: 8, category: 'Cleanup' },
      { id: 'it_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 9, category: 'Cleanup', suffix: '/10' },
      { id: 'it_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 10, category: 'Cleanup' },
    ],
  },
  {
    name: 'VoIP Install',
    dealCategory: 'VOIP',
    jobType: 'Install',
    isDefault: true,
    checkpoints: [
      { id: 'voip_install_handsets', label: 'Handsets installed and powered', type: 'checkbox', required: true, order: 1, category: 'Setup' },
      { id: 'voip_handset_count', label: 'Number of handsets', type: 'text', required: false, order: 2, category: 'Setup', placeholder: 'e.g. 3' },
      { id: 'voip_lines_configured', label: 'Lines configured and registered', type: 'checkbox', required: true, order: 3, category: 'Setup' },
      { id: 'voip_inbound_tested', label: 'Inbound calls tested', type: 'checkbox', required: true, order: 4, category: 'Testing' },
      { id: 'voip_outbound_tested', label: 'Outbound calls tested', type: 'checkbox', required: true, order: 5, category: 'Testing' },
      { id: 'voip_call_quality', label: 'Call quality', type: 'select', required: true, order: 6, category: 'Testing', options: ['Excellent', 'Good', 'Acceptable', 'Poor'] },
      { id: 'voip_porting', label: 'Number porting confirmed (if applicable)', type: 'checkbox', required: false, order: 7, category: 'Testing' },
      { id: 'voip_voicemail', label: 'Voicemail configured', type: 'checkbox', required: false, order: 8, category: 'Testing' },
      { id: 'voip_customer_training', label: 'Customer trained on features', type: 'checkbox', required: true, order: 9, category: 'Cleanup' },
      { id: 'voip_customer_sentiment', label: 'Customer sentiment', type: 'rating', required: false, order: 10, category: 'Cleanup', suffix: '/10' },
      { id: 'voip_cleanup', label: 'Clean up work area', type: 'checkbox', required: true, order: 11, category: 'Cleanup' },
    ],
  },
  // Fault templates (simpler - just diagnosis + fix)
  {
    name: 'General Fault',
    dealCategory: 'NBN',
    jobType: 'Fault',
    isDefault: true,
    checkpoints: [
      { id: 'fault_verify_symptom', label: 'Verify reported symptom', type: 'checkbox', required: true, order: 1, category: 'Diagnosis' },
      { id: 'fault_check_hardware', label: 'Check hardware connections', type: 'checkbox', required: true, order: 2, category: 'Diagnosis' },
      { id: 'fault_diagnosis', label: 'Root cause identified', type: 'text', required: true, order: 3, category: 'Diagnosis' },
      { id: 'fault_fix_applied', label: 'Fix applied', type: 'text', required: true, order: 4, category: 'Resolution' },
      { id: 'fault_verify_fix', label: 'Verify fix resolves issue', type: 'checkbox', required: true, order: 5, category: 'Resolution' },
      { id: 'fault_speed_test', label: 'Speed test after fix', type: 'text', required: false, order: 6, category: 'Resolution', placeholder: 'e.g. 100/40 Mbps', suffix: 'Mbps' },
    ],
  },
];
